import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Settings as SettingsIcon, ArrowRight, Mail, MessageSquare } from 'lucide-react';

/**
 * Professional warning modal shown when a required configuration
 * (SMTP or SMS) has not been completed before trying to send a message.
 */
const SettingsRequiredModal = ({
    isOpen,
    onClose,
    type = 'email', // 'email' | 'sms'
    canManageSettings = true
}) => {
    const isEmail = type === 'email';

    const config = isEmail
        ? {
            title: 'SMTP Configuration Required',
            Icon: Mail,
            accent: 'blue',
            description: 'Before sending emails, you need to configure your SMTP settings. This ensures emails are delivered reliably using your own mail server.',
            requirementsLabel: 'Required fields:',
            requirements: ['SMTP Host & Port', 'Authentication Credentials', 'Sender Email Address'],
            href: '/smtp-settings',
            ctaLabel: 'Configure SMTP Settings'
        }
        : {
            title: 'SMS Configuration Required',
            Icon: MessageSquare,
            accent: 'teal',
            description: 'SMS is delivered by Gain Rhino VoIP — the same account that powers your call integration. Connect Gain Rhino VoIP under Settings → VoIP to unlock SMS sending.',
            requirementsLabel: 'Required setup:',
            requirements: ['Gain Rhino VoIP API Username', 'Gain Rhino VoIP API Password', 'A DID with SMS enabled'],
            href: '/voip-settings',
            ctaLabel: 'Open VoIP Settings'
        };

    const accentClasses = {
        blue: {
            iconBg: 'bg-blue-50',
            iconColor: 'text-blue-600',
            button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
            bullet: 'bg-blue-500'
        },
        teal: {
            iconBg: 'bg-teal-50',
            iconColor: 'text-teal-600',
            button: 'bg-teal-600 hover:bg-teal-700 focus:ring-teal-500',
            bullet: 'bg-teal-500'
        }
    }[config.accent];

    const { Icon } = config;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 transition-opacity"
                    />

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
                                <div className="flex items-center space-x-4">
                                    <div className={`flex-shrink-0 w-12 h-12 rounded-full ${accentClasses.iconBg} flex items-center justify-center relative`}>
                                        <Icon className={`w-6 h-6 ${accentClasses.iconColor}`} />
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center">
                                            <AlertTriangle className="w-3 h-3 text-amber-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            {config.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Action required to continue
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Close"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-6 space-y-4">
                                <p className="text-gray-600 leading-relaxed text-sm">
                                    {config.description}
                                </p>

                                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                        {config.requirementsLabel}
                                    </p>
                                    <ul className="space-y-2">
                                        {config.requirements.map((item) => (
                                            <li key={item} className="flex items-center text-sm text-gray-700">
                                                <span className={`w-1.5 h-1.5 rounded-full ${accentClasses.bullet} mr-3 flex-shrink-0`} />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {!canManageSettings && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                                        You do not have permission to manage these settings. Please contact your administrator.
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-100">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                {canManageSettings && (
                                    <Link
                                        to={config.href}
                                        onClick={onClose}
                                        className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${accentClasses.button}`}
                                    >
                                        <SettingsIcon className="w-4 h-4 mr-2" />
                                        {config.ctaLabel}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Link>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SettingsRequiredModal;
